import { Button, Checkbox, ChoiceRow, Field, FieldGrid, Input, Select, cn } from '@westhetics/ui'
import type { ChangeEvent, ReactNode } from 'react'
import { FieldNote, touchField } from '@/app/public-screen'
import { noteText, supportText } from '@/app/shell'
import {
  maskCpf,
  maskDate,
  maskInstagram,
  maskPhone,
  maskPostalCode,
  maskWhatsapp,
} from '@/lib/masks'
import type { ForeignDocumentType, Gender, MaritalStatus, ReferralChannel } from './api'
import { STEP_TITLES, type FieldKey, type RegisterValues, type StepId } from './form'
import {
  DIAL_CODES,
  FOREIGN_DOCUMENT_TYPE_LABELS,
  GENDER_LABELS,
  MARITAL_STATUS_LABELS,
  REFERRAL_CHANNEL_LABELS,
  STATES,
} from './labels'

/**
 * As etapas do cadastro pelo link (T52): "Seus dados", "Contato e endereço", "Responsável legal"
 * (menor de idade, ou quando marcado) e "Conferir e enviar". Cada etapa só desenha os campos; a
 * validação e o envio são de `register-page.tsx` e `form.ts`.
 */

type TextKey = {
  [K in keyof RegisterValues]: RegisterValues[K] extends string ? K : never
}[keyof RegisterValues]

export interface StepProps {
  values: RegisterValues
  update: <K extends keyof RegisterValues>(key: K, value: RegisterValues[K]) => void
  errorOf: (key: FieldKey) => string | undefined
  /** Marca o campo como visitado: o erro dele aparece daí em diante. */
  touch: (key: FieldKey) => void
  fieldId: (key: FieldKey) => string
}

/** Valor, mudança e saída de um campo de texto. O erro nasce ao sair do campo, depois de digitar. */
function textProps(p: StepProps, key: TextKey, mask?: (text: string) => string) {
  return {
    value: p.values[key],
    className: touchField,
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      p.update(key, mask ? mask(event.target.value) : event.target.value),
    onBlur: () => {
      if (p.values[key].trim() !== '') p.touch(key)
    },
  }
}

/** Props de um `<Select>` ligado a um campo; a escolha conta como visita. */
function selectProps<K extends TextKey>(p: StepProps, key: K) {
  return {
    value: p.values[key],
    className: touchField,
    onChange: (event: ChangeEvent<HTMLSelectElement>) => {
      p.update(key, event.target.value as RegisterValues[K])
      p.touch(key)
    },
  }
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="font-display text-ink mt-9 text-[15px] leading-[1.35] font-normal">
      {children}
    </h3>
  )
}

// 1 · Seus dados ----------------------------------------------------------------------------------

export function YouStep({
  minor,
  countries,
  ...p
}: StepProps & { minor: boolean; countries: { code: string; name: string }[] }) {
  const { values, errorOf, fieldId } = p
  return (
    <>
      <FieldGrid className="mt-5">
        <Field
          id={fieldId('name')}
          label="Nome completo"
          error={errorOf('name')}
          className="tablet:col-span-2"
        >
          <Input
            {...textProps(p, 'name')}
            autoComplete="name"
            autoCapitalize="words"
            enterKeyHint="next"
            maxLength={120}
          />
        </Field>
        <Field
          id={fieldId('birthDate')}
          label="Data de nascimento"
          optional
          error={errorOf('birthDate')}
          hint="Diz à clínica se o contrato precisa do responsável legal."
        >
          <Input
            {...textProps(p, 'birthDate', maskDate)}
            inputMode="numeric"
            autoComplete="bday"
            enterKeyHint="next"
            placeholder="dd/mm/aaaa"
            maxLength={10}
          />
        </Field>
        {values.hasNoCpf ? (
          <Field id={fieldId('foreignType')} label="Tipo de documento">
            <Select
              value={values.foreignType}
              className={touchField}
              onChange={(event) =>
                p.update('foreignType', event.target.value as ForeignDocumentType)
              }
            >
              {Object.entries(FOREIGN_DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field
            id={fieldId('docNumber')}
            label="CPF"
            optional
            error={errorOf('docNumber')}
            hint="A clínica precisa do CPF para o contrato."
          >
            <Input
              {...textProps(p, 'docNumber', maskCpf)}
              inputMode="numeric"
              autoComplete="off"
              enterKeyHint="next"
              placeholder="000.000.000-00"
              maxLength={14}
            />
          </Field>
        )}
        {values.hasNoCpf ? (
          <>
            <Field
              id={fieldId('foreignNumber')}
              label="Número do documento"
              error={errorOf('foreignNumber')}
            >
              <Input
                {...textProps(p, 'foreignNumber')}
                autoComplete="off"
                autoCapitalize="characters"
                enterKeyHint="next"
                maxLength={20}
              />
            </Field>
            <Field
              id={fieldId('foreignCountry')}
              label="País que emitiu"
              error={errorOf('foreignCountry')}
            >
              <Select {...selectProps(p, 'foreignCountry')} placeholder="Escolha o país">
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : null}
        <Field id={fieldId('rg')} label="RG" optional error={errorOf('rg')}>
          <Input {...textProps(p, 'rg')} autoComplete="off" enterKeyHint="next" maxLength={24} />
        </Field>
      </FieldGrid>

      <ChoiceRow
        className="mt-4"
        control={
          <Checkbox
            checked={values.hasNoCpf}
            onCheckedChange={(checked) => p.update('hasNoCpf', checked === true)}
          />
        }
        description="Passaporte, RNE ou CRNM entram no lugar do CPF no cadastro e no contrato."
      >
        Não tenho CPF
      </ChoiceRow>

      <FieldGrid className="mt-6">
        <Field
          id={fieldId('maritalStatus')}
          label="Estado civil"
          optional
          error={errorOf('maritalStatus')}
        >
          <Select {...selectProps(p, 'maritalStatus')} placeholder="Escolha o estado civil">
            {Object.entries(MARITAL_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value as MaritalStatus}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field id={fieldId('occupation')} label="Profissão" optional error={errorOf('occupation')}>
          <Input
            {...textProps(p, 'occupation')}
            autoComplete="organization-title"
            enterKeyHint="next"
            maxLength={80}
          />
        </Field>
        <Field
          id={fieldId('gender')}
          label="Gênero"
          optional
          error={errorOf('gender')}
          hint={'Só ajusta a concordância do contrato, como "a contratante".'}
        >
          <Select {...selectProps(p, 'gender')} placeholder="Escolha uma opção">
            {Object.entries(GENDER_LABELS).map(([value, label]) => (
              <option key={value} value={value as Gender}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
      </FieldGrid>

      {minor ? (
        <p className={cn(supportText, 'border-hairline-strong mt-6 border-l-2 pl-4')}>
          Como você tem menos de 18 anos, o próximo passo pede o responsável legal. É ele quem
          assina os documentos por você.
        </p>
      ) : (
        <ChoiceRow
          className="mt-6"
          control={
            <Checkbox
              checked={values.hasGuardian}
              onCheckedChange={(checked) => p.update('hasGuardian', checked === true)}
            />
          }
          description="Tutela ou curatela: quem assina os documentos por você."
        >
          Tenho responsável legal
        </ChoiceRow>
      )}
    </>
  )
}

// 2 · Contato e endereço --------------------------------------------------------------------------

export function ContactStep({
  postalCodeHint,
  onPostalCode,
  countries,
  emailLocked = false,
  emailChange,
  ...p
}: StepProps & {
  /** "Buscando o endereço…" ou a frase da falha da consulta: dica, nunca erro. */
  postalCodeHint: string | undefined
  /** O CEP mudou (já com máscara): a página consulta quando tem 8 dígitos. */
  onPostalCode: (masked: string) => void
  countries: { code: string; name: string }[]
  /** O e-mail veio confirmado pelo código (RN-PAC-55): não se digita, troca-se com outro código. */
  emailLocked?: boolean
  /** A ação "Trocar e-mail" e o painel dela, quando o e-mail está travado. */
  emailChange?: ReactNode
}) {
  const { values, errorOf, fieldId } = p
  const abroad = values.country !== 'BR'
  return (
    <>
      <FieldGrid className="mt-5">
        <Field id={fieldId('dialCode')} label="País do número" error={errorOf('dialCode')}>
          <Select
            value={values.dialCode}
            className={touchField}
            onChange={(event) => {
              p.update('dialCode', event.target.value)
              p.update('whatsapp', maskWhatsapp(values.whatsapp, event.target.value))
            }}
          >
            {DIAL_CODES.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          id={fieldId('whatsapp')}
          label="WhatsApp"
          error={errorOf('whatsapp')}
          hint="É por ele que a clínica manda a anamnese e os documentos."
        >
          <Input
            {...textProps(p, 'whatsapp', (text) => maskWhatsapp(text, values.dialCode))}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            enterKeyHint="next"
            placeholder={values.dialCode === '+55' ? '(11) 99999-0000' : undefined}
            maxLength={18}
          />
        </Field>
        <Field
          id={fieldId('email')}
          label="E-mail"
          optional={!emailLocked}
          error={errorOf('email')}
          hint={
            emailLocked
              ? 'Confirmado pelo código. Para usar outro, troque com um código no e-mail novo.'
              : 'Com o e-mail você entra no portal do paciente e vê seus documentos.'
          }
        >
          <Input
            {...textProps(p, 'email')}
            readOnly={emailLocked || undefined}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            maxLength={254}
          />
        </Field>
        {emailLocked && emailChange ? <div className="tablet:col-span-2">{emailChange}</div> : null}
        <Field
          id={fieldId('instagram')}
          label="Instagram"
          optional
          error={errorOf('instagram')}
          hint="Só o nome de usuário, sem @."
        >
          <Input
            {...textProps(p, 'instagram', maskInstagram)}
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            maxLength={30}
          />
        </Field>
      </FieldGrid>

      <SectionTitle>
        Endereço<span className="text-label-soft"> · opcional</span>
      </SectionTitle>
      <p className={cn(noteText, 'mt-1')}>
        Entra no contrato. Começou, preencha inteiro; se preferir, deixe para a recepção.
      </p>
      <FieldGrid className="mt-4">
        <Field id={fieldId('country')} label="País" error={errorOf('country')}>
          <Select
            value={values.country}
            className={touchField}
            autoComplete="country"
            onChange={(event) => {
              p.update('country', event.target.value)
              // A UF do Brasil é da lista; o estado de fora é texto. Trocar o país recomeça os dois.
              p.update('state', '')
              p.update('postalCode', '')
            }}
          >
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          id={fieldId('postalCode')}
          label={abroad ? 'Código postal' : 'CEP'}
          error={errorOf('postalCode')}
          hint={
            abroad ? undefined : (postalCodeHint ?? 'Com o CEP, a rua e a cidade vêm sozinhas.')
          }
        >
          <Input
            value={values.postalCode}
            className={touchField}
            onChange={(event) =>
              abroad
                ? p.update('postalCode', event.target.value)
                : onPostalCode(maskPostalCode(event.target.value))
            }
            onBlur={() => {
              if (values.postalCode.trim() !== '') p.touch('postalCode')
            }}
            inputMode={abroad ? 'text' : 'numeric'}
            autoComplete="postal-code"
            enterKeyHint="next"
            placeholder={abroad ? undefined : '00000-000'}
            maxLength={abroad ? 20 : 9}
          />
        </Field>
        <Field
          id={fieldId('street')}
          label="Rua"
          error={errorOf('street')}
          className="tablet:col-span-2"
        >
          <Input
            {...textProps(p, 'street')}
            autoComplete="address-line1"
            enterKeyHint="next"
            maxLength={120}
          />
        </Field>
        <Field
          id={fieldId('number')}
          label="Número"
          error={errorOf('number')}
          hint="Sem número, escreva s/n."
        >
          <Input
            {...textProps(p, 'number')}
            autoComplete="off"
            enterKeyHint="next"
            maxLength={10}
          />
        </Field>
        <Field
          id={fieldId('complement')}
          label="Complemento"
          optional
          error={errorOf('complement')}
        >
          <Input
            {...textProps(p, 'complement')}
            autoComplete="address-line2"
            enterKeyHint="next"
            maxLength={60}
          />
        </Field>
        <Field id={fieldId('district')} label="Bairro" error={errorOf('district')}>
          <Input
            {...textProps(p, 'district')}
            autoComplete="address-level3"
            enterKeyHint="next"
            maxLength={80}
          />
        </Field>
        <Field id={fieldId('city')} label="Cidade" error={errorOf('city')}>
          <Input
            {...textProps(p, 'city')}
            autoComplete="address-level2"
            enterKeyHint="next"
            maxLength={80}
          />
        </Field>
        {abroad ? (
          <Field id={fieldId('state')} label="Estado ou província" error={errorOf('state')}>
            <Input
              {...textProps(p, 'state')}
              autoComplete="address-level1"
              enterKeyHint="done"
              maxLength={60}
            />
          </Field>
        ) : (
          <Field id={fieldId('state')} label="UF" error={errorOf('state')}>
            <Select {...selectProps(p, 'state')} placeholder="Escolha a UF">
              {STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FieldGrid>
    </>
  )
}

// 3 · Responsável legal ---------------------------------------------------------------------------

export function GuardianStep({ minor, ...p }: StepProps & { minor: boolean }) {
  const { errorOf, fieldId } = p
  return (
    <>
      <p className={cn(supportText, 'mt-3')}>
        {minor
          ? 'Como você tem menos de 18 anos, o cadastro vai com o responsável legal. É ele quem assina os documentos por você.'
          : 'Quem responde por você, por tutela ou curatela, e assina os documentos.'}
      </p>
      <FieldGrid className="mt-5">
        <Field
          id={fieldId('guardianName')}
          label="Nome do responsável"
          error={errorOf('guardianName')}
          className="tablet:col-span-2"
        >
          <Input
            {...textProps(p, 'guardianName')}
            autoComplete="off"
            autoCapitalize="words"
            enterKeyHint="next"
            maxLength={120}
          />
        </Field>
        <Field
          id={fieldId('guardianDocNumber')}
          label="CPF do responsável"
          error={errorOf('guardianDocNumber')}
        >
          <Input
            {...textProps(p, 'guardianDocNumber', maskCpf)}
            inputMode="numeric"
            autoComplete="off"
            enterKeyHint="next"
            placeholder="000.000.000-00"
            maxLength={14}
          />
        </Field>
        <Field
          id={fieldId('guardianWhatsapp')}
          label="WhatsApp do responsável"
          error={errorOf('guardianWhatsapp')}
        >
          <Input
            {...textProps(p, 'guardianWhatsapp', maskPhone)}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            enterKeyHint="done"
            placeholder="(11) 99999-0000"
            maxLength={15}
          />
        </Field>
      </FieldGrid>
    </>
  )
}

// 4 · Conferir e enviar ---------------------------------------------------------------------------

export interface SummaryGroup {
  step: StepId
  lines: string[]
}

export function ReviewStep({
  clinicName,
  privacyNotice,
  privacyAccepted,
  onPrivacy,
  summary,
  onEdit,
  ...p
}: StepProps & {
  clinicName: string
  privacyNotice: string
  privacyAccepted: boolean
  onPrivacy: (accepted: boolean) => void
  summary: SummaryGroup[]
  onEdit: (step: StepId) => void
}) {
  const { values, errorOf, fieldId } = p
  return (
    <>
      <div className="mt-5 flex flex-col">
        {summary.map((group) => (
          <section
            key={group.step}
            aria-label={STEP_TITLES[group.step]}
            className="border-hairline flex items-start justify-between gap-4 border-b py-4 first:border-t"
          >
            <div className="min-w-0">
              <div className="eyebrow">{STEP_TITLES[group.step]}</div>
              {group.lines.map((line) => (
                <p key={line} className={cn(supportText, 'text-ink mt-1 break-words')}>
                  {line}
                </p>
              ))}
            </div>
            <Button
              type="button"
              variant="text"
              size="sm"
              aria-label={`Alterar ${STEP_TITLES[group.step].toLowerCase()}`}
              onClick={() => onEdit(group.step)}
            >
              Alterar
            </Button>
          </section>
        ))}
      </div>

      <FieldGrid className="mt-6">
        <Field
          id={fieldId('referralChannel')}
          label={`Como conheceu a ${clinicName}`}
          optional
          error={errorOf('referralChannel')}
        >
          <Select {...selectProps(p, 'referralChannel')} placeholder="Escolha uma opção">
            {Object.entries(REFERRAL_CHANNEL_LABELS).map(([value, label]) => (
              <option key={value} value={value as ReferralChannel}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        {values.referralChannel ? (
          <Field
            id={fieldId('referralDetail')}
            label="Detalhe"
            optional
            error={errorOf('referralDetail')}
            hint="Quem indicou, qual anúncio, qual evento."
          >
            <Input {...textProps(p, 'referralDetail')} autoComplete="off" maxLength={120} />
          </Field>
        ) : null}
      </FieldGrid>

      <ChoiceRow
        className="mt-7"
        control={
          <Checkbox
            id={fieldId('privacyAccepted')}
            checked={privacyAccepted}
            onCheckedChange={(checked) => onPrivacy(checked === true)}
            aria-invalid={errorOf('privacyAccepted') ? true : undefined}
            aria-describedby={`${fieldId('privacyAccepted')}-note`}
          />
        }
      >
        {privacyNotice}
      </ChoiceRow>
      <FieldNote id={`${fieldId('privacyAccepted')}-note`} error={errorOf('privacyAccepted')} />
    </>
  )
}
